import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import {
    motion,
    useScroll,
    useTransform,
    useInView,
    AnimatePresence,
} from "framer-motion";
import {
    ArrowRight,
    ArrowUpRight,
    Check,
    Quote,
    Plus,
    Minus,
} from "lucide-react";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";

/* ---------- Small motion helpers ---------- */

const easeOutExpo = [0.16, 1, 0.3, 1];

function LineReveal({ children, delay = 0, className = "" }) {
    return (
        <span className={`line-mask ${className}`}>
            <motion.span
                className="inline-block will-change-transform"
                initial={{ y: "110%" }}
                animate={{ y: "0%" }}
                transition={{ duration: 1.1, delay, ease: easeOutExpo }}
            >
                {children}
            </motion.span>
        </span>
    );
}

function FadeUp({ children, delay = 0, y = 30, className = "" }) {
    const ref = useRef(null);
    const inView = useInView(ref, { once: true, amount: 0.2 });
    return (
        <motion.div
            ref={ref}
            initial={{ opacity: 0, y }}
            animate={inView ? { opacity: 1, y: 0 } : { opacity: 0, y }}
            transition={{ duration: 0.9, delay, ease: easeOutExpo }}
            className={className}
        >
            {children}
        </motion.div>
    );
}

/* ---------- Hero ---------- */

function Hero() {
    const heroRef = useRef(null);
    const { scrollYProgress } = useScroll({
        target: heroRef,
        offset: ["start start", "end start"],
    });
    const imgY = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);
    const imgScale = useTransform(scrollYProgress, [0, 1], [1, 1.08]);
    const textY = useTransform(scrollYProgress, [0, 1], ["0%", "-15%"]);
    const overlay = useTransform(scrollYProgress, [0, 1], [0.4, 0.75]);

    return (
        <section
            ref={heroRef}
            data-testid="hero-section"
            className="relative min-h-[100svh] w-full overflow-hidden bg-[#FDFAF5] grain"
        >
            {/* Backdrop image with parallax + spotlight mask */}
            <motion.div
                style={{ y: imgY, scale: imgScale }}
                className="absolute inset-0 -z-10"
            >
                <div className="absolute inset-0 spotlight-mask overflow-hidden">
                    <img
                        src="https://images.unsplash.com/photo-1601591562068-1cf58f6b5c3f?auto=format&fit=crop&w=2400&q=85"
                        alt=""
                        className="w-full h-full object-cover"
                    />
                </div>
                <motion.div
                    style={{ opacity: overlay }}
                    className="absolute inset-0 bg-gradient-to-b from-[#FDFAF5] via-[#FDFAF5]/70 to-[#0D1B1A]"
                />
            </motion.div>

            {/* Corner marks */}
            <div className="absolute top-24 left-6 md:left-12 z-10 flex items-center gap-3 text-[#0D1B1A]/80">
                <motion.span
                    initial={{ scaleX: 0 }}
                    animate={{ scaleX: 1 }}
                    transition={{ duration: 1.2, delay: 0.2, ease: easeOutExpo }}
                    className="ornament origin-left"
                />
                <span className="text-[10px] tracking-[0.35em] uppercase">
                    Chapter 00 · The Overture
                </span>
            </div>

            <div className="absolute top-24 right-6 md:right-12 z-10 text-right text-[10px] tracking-[0.3em] uppercase text-[#0D1B1A]/70">
                <div>Est. 2025</div>
                <div className="mt-1 text-[#D4944C]">Mumbai · India</div>
            </div>

            {/* Main content */}
            <motion.div
                style={{ y: textY }}
                className="relative z-10 pt-40 md:pt-48 pb-32 md:pb-40 px-6 md:px-12 max-w-7xl mx-auto"
            >
                <h1 className="font-serif text-[13vw] md:text-[10vw] leading-[0.92] tracking-[-0.04em] text-[#0D1B1A] font-light">
                    <LineReveal delay={0.35}>
                        <span className="italic font-normal">From</span> EMI
                    </LineReveal>
                    <LineReveal delay={0.5}>stress to</LineReveal>
                    <LineReveal delay={0.65}>
                        <span className="text-[#D4944C] italic font-normal">
                            financial
                        </span>{" "}
                        clarity.
                    </LineReveal>
                </h1>

                <div className="mt-16 md:mt-24 grid md:grid-cols-12 gap-8 items-end">
                    <motion.div
                        initial={{ opacity: 0, y: 40 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{
                            duration: 0.9,
                            delay: 1.0,
                            ease: easeOutExpo,
                        }}
                        className="md:col-span-5"
                    >
                        <p className="text-lg text-[#0D1B1A]/80 max-w-md leading-relaxed">
                            A wealth strategy and debt-free advisory for Indian
                            professionals and families. Built on 20+ years of
                            sitting inside actual balance sheets — not on
                            YouTube.
                        </p>

                        <div className="mt-10 flex flex-col sm:flex-row gap-3">
                            <a
                                href="#packages"
                                data-testid="hero-cta-book"
                                className="group relative bg-[#0D1B1A] text-[#FDFAF5] hover:bg-[#1A5C53] transition-colors duration-500 rounded-full px-6 py-4 font-medium text-sm tracking-wide flex items-center justify-center gap-3 overflow-hidden"
                            >
                                <span className="relative z-10">
                                    Choose your package
                                </span>
                                <ArrowUpRight
                                    size={16}
                                    className="relative z-10 group-hover:rotate-45 transition-transform duration-500"
                                />
                            </a>
                            <a
                                href="#how"
                                data-testid="hero-cta-packages"
                                className="group inline-flex items-center justify-center gap-2 text-[#0D1B1A] text-sm font-medium tracking-wide px-2 py-4"
                            >
                                <span className="link-underline">
                                    How it works
                                </span>
                                <ArrowRight
                                    size={14}
                                    className="group-hover:translate-x-1 transition-transform"
                                />
                            </a>
                        </div>
                    </motion.div>

                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        transition={{ duration: 1.4, delay: 1.2 }}
                        className="md:col-span-6 md:col-start-7 grid grid-cols-3 gap-6"
                    >
                        {[
                            { k: "20+", v: "Years in finance" },
                            { k: "500+", v: "Families advised" },
                            { k: "₹50Cr", v: "Debt restructured" },
                        ].map((s, i) => (
                            <motion.div
                                key={s.v}
                                initial={{ opacity: 0, y: 20 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{
                                    duration: 0.9,
                                    delay: 1.3 + i * 0.1,
                                    ease: easeOutExpo,
                                }}
                                className="border-t border-[#0D1B1A]/30 pt-3"
                            >
                                <div className="font-serif text-3xl md:text-5xl text-[#0D1B1A]">
                                    {s.k}
                                </div>
                                <div className="text-[10px] uppercase tracking-[0.2em] text-[#0D1B1A]/60 mt-2">
                                    {s.v}
                                </div>
                            </motion.div>
                        ))}
                    </motion.div>
                </div>
            </motion.div>

            {/* Scroll cue */}
            <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 2, duration: 1 }}
                className="absolute bottom-8 left-1/2 -translate-x-1/2 z-10 text-[10px] uppercase tracking-[0.4em] text-[#FDFAF5] flex flex-col items-center gap-3"
            >
                <span>Scroll</span>
                <motion.div
                    animate={{ y: [0, 8, 0] }}
                    transition={{ duration: 2, repeat: Infinity }}
                    className="w-px h-8 bg-[#FDFAF5]/60"
                />
            </motion.div>
        </section>
    );
}

/* ---------- Marquee ---------- */

function Marquee() {
    const words = [
        "Debt-free is the new wealthy",
        "★",
        "Named debt is tamed debt",
        "★",
        "Minimum due is quiet robbery",
        "★",
        "Every rate is negotiable",
        "★",
        "Freedom is a date, not a feeling",
        "★",
    ];
    const track = [...words, ...words];
    return (
        <div
            data-testid="marquee-strip"
            className="bg-[#0D1B1A] text-[#FDFAF5] py-8 border-y border-[#1F3533]"
        >
            <div className="marquee">
                <div className="marquee-track font-editorial italic text-3xl md:text-5xl">
                    {track.map((w, i) => (
                        <span
                            key={i}
                            className={
                                w === "★"
                                    ? "text-[#D4944C] not-italic text-2xl md:text-4xl self-center"
                                    : ""
                            }
                        >
                            {w}
                        </span>
                    ))}
                </div>
            </div>
        </div>
    );
}

/* ---------- Problem (bento) ---------- */

function ProblemSection() {
    const items = [
        {
            span: "md:col-span-7 md:row-span-2",
            bg: "bg-white",
            tone: "dark",
            body: (
                <>
                    <div className="text-xs tracking-[0.25em] uppercase text-[#D4944C]">
                        The minimum-due trap
                    </div>
                    <h3 className="font-serif text-4xl md:text-5xl mt-6 text-[#0D1B1A] leading-[1.02]">
                        A ₹1 lakh card bill becomes ₹3.7 lakhs of interest.{" "}
                        <span className="italic text-[#1A5C53]">
                            Paid slowly, in shame.
                        </span>
                    </h3>
                    <p className="mt-6 text-[#4A5A58] max-w-md">
                        The bank designs the statement to make you focus on the
                        minimum due. We're going to un-design that.
                    </p>
                </>
            ),
        },
        {
            span: "md:col-span-5",
            bg: "bg-[#0D1B1A]",
            tone: "light",
            body: (
                <>
                    <div className="font-serif text-[110px] md:text-[140px] leading-none text-[#D4944C]">
                        40%
                    </div>
                    <p className="text-sm text-[#9FB1AE] mt-4">
                        of take-home going to EMIs? You're officially in the
                        danger zone.
                    </p>
                </>
            ),
        },
        {
            span: "md:col-span-5",
            bg: "bg-[#F3EFE9]",
            tone: "dark",
            body: (
                <>
                    <div className="text-xs tracking-[0.25em] uppercase text-[#1A5C53]">
                        Multiple loans
                    </div>
                    <h4 className="font-serif text-2xl md:text-3xl mt-4 text-[#0D1B1A]">
                        Home, car, personal, education — four lenders, zero
                        master plan.
                    </h4>
                </>
            ),
        },
        {
            span: "md:col-span-6",
            bg: "bg-[#1A5C53]",
            tone: "light",
            body: (
                <>
                    <div className="text-xs tracking-[0.25em] uppercase text-[#D4944C]">
                        Rising rates
                    </div>
                    <h4 className="font-serif text-2xl md:text-3xl mt-4 text-white">
                        Interest climbs faster than salary. The EMI grows; the
                        buffer shrinks.
                    </h4>
                </>
            ),
        },
        {
            span: "md:col-span-6",
            bg: "bg-white",
            tone: "dark",
            body: (
                <>
                    <div className="text-xs tracking-[0.25em] uppercase text-[#1A5C53]">
                        No roadmap
                    </div>
                    <h4 className="font-serif text-2xl md:text-3xl mt-4 text-[#0D1B1A]">
                        Without a debt-free date, every month is survival —
                        not progress.
                    </h4>
                </>
            ),
        },
    ];

    return (
        <section
            id="problem"
            data-testid="problem-section"
            className="py-28 md:py-40 bg-[#FDFAF5]"
        >
            <div className="max-w-7xl mx-auto px-6 md:px-12">
                <div className="grid md:grid-cols-12 items-end gap-6 mb-16">
                    <FadeUp className="md:col-span-6">
                        <div className="flex items-center gap-3 mb-6">
                            <span className="ornament" />
                            <span className="text-[10px] tracking-[0.3em] uppercase text-[#1A5C53]">
                                Chapter 01 · The Diagnosis
                            </span>
                        </div>
                        <h2 className="font-serif text-5xl md:text-6xl leading-[1.02] text-[#0D1B1A]">
                            The quiet pressure of{" "}
                            <span className="italic">modern debt.</span>
                        </h2>
                    </FadeUp>
                    <FadeUp delay={0.1} className="md:col-span-5 md:col-start-8">
                        <p className="text-[#4A5A58] text-lg">
                            Most of us aren't broke. We're bleeding money in
                            ways we never audit.
                        </p>
                    </FadeUp>
                </div>

                <div className="grid md:grid-cols-12 gap-6 auto-rows-[minmax(0,1fr)]">
                    {items.map((it, i) => (
                        <FadeUp
                            key={i}
                            delay={i * 0.06}
                            y={40}
                            className={`${it.span} ${it.bg} rounded-2xl p-8 md:p-10 border border-[#E6DFD3]/50 flex flex-col justify-between min-h-[220px] ${it.tone === "light" ? "text-white" : "text-[#0D1B1A]"}`}
                        >
                            <div>{it.body}</div>
                        </FadeUp>
                    ))}
                </div>
            </div>
        </section>
    );
}

/* ---------- Manifesto (Numbered chapters replacing "How It Works") ---------- */

function ManifestoSection() {
    const chapters = [
        {
            n: "I",
            eyebrow: "The Reveal",
            title: "Tell us your numbers.",
            body: "A confidential questionnaire — income, EMIs, expenses, goals. Honest inputs, sharper insights. The audit alone shifts something.",
        },
        {
            n: "II",
            eyebrow: "The Blueprint",
            title: "We build your roadmap.",
            body: "Our team drafts a personalised debt-restructuring and wealth plan. Every rupee has a role. Every month has a target.",
        },
        {
            n: "III",
            eyebrow: "The Walk",
            title: "You execute — we guide.",
            body: "Step-by-step execution, monthly check-ins, and where debts are severe, an introduction to our legal team for settlement.",
        },
    ];

    return (
        <section
            id="how"
            data-testid="how-section"
            className="relative py-28 md:py-40 bg-[#F3EFE9]/60"
        >
            <div className="max-w-7xl mx-auto px-6 md:px-12">
                <FadeUp>
                    <div className="flex items-center gap-3 mb-4">
                        <span className="ornament" />
                        <span className="text-[10px] tracking-[0.3em] uppercase text-[#1A5C53]">
                            Chapter 02 · The Manifesto
                        </span>
                    </div>
                    <h2 className="font-serif text-5xl md:text-6xl leading-[1.02] text-[#0D1B1A] max-w-3xl">
                        Three moves.{" "}
                        <span className="italic">One roadmap.</span> Lifelong
                        clarity.
                    </h2>
                </FadeUp>

                <div className="mt-16 md:mt-24 space-y-6 md:space-y-2">
                    {chapters.map((c, i) => (
                        <FadeUp key={c.n} delay={i * 0.08} y={40}>
                            <article className="group grid md:grid-cols-12 gap-6 md:gap-10 items-start py-10 md:py-14 border-t border-[#0D1B1A]/12 hover:bg-[#FDFAF5]/60 transition-colors duration-500 px-2 md:px-4 rounded-xl">
                                <div className="md:col-span-3">
                                    <div className="chapter-numeral">{c.n}</div>
                                </div>
                                <div className="md:col-span-6">
                                    <div className="text-[10px] tracking-[0.35em] uppercase text-[#D4944C] mb-4">
                                        {c.eyebrow}
                                    </div>
                                    <h3 className="font-serif text-3xl md:text-5xl text-[#0D1B1A] leading-[1.05]">
                                        {c.title}
                                    </h3>
                                </div>
                                <div className="md:col-span-3">
                                    <p className="text-[#4A5A58] leading-relaxed">
                                        {c.body}
                                    </p>
                                </div>
                            </article>
                        </FadeUp>
                    ))}
                </div>
            </div>
        </section>
    );
}

/* ---------- Packages ---------- */

function PackagesSection() {
    const pkgs = [
        {
            key: "disha",
            name: "Disha",
            tag: "Quick Health Check",
            price: "969",
            items: [
                "Confidential questionnaire",
                "Written health-check report",
                "Top 3 action items",
                "48-hour turnaround",
            ],
            featured: false,
        },
        {
            key: "niti",
            name: "Niti",
            tag: "Strategic Assessment",
            price: "1,969",
            items: [
                "Detailed 70+ point assessment",
                "Complete debt restructuring plan",
                "Income optimisation roadmap",
                "Monthly expense rebuild",
                "30-day follow-up",
            ],
            featured: true,
        },
        {
            key: "samvad",
            name: "Samvad",
            tag: "1-on-1 Consultation",
            price: "3,969",
            items: [
                "Everything in Niti",
                "1-hour live consultation",
                "Custom document review",
                "Introduction to our legal team for debt settlement*",
            ],
            featured: false,
            note: "*Legal team can help settle debts with banks & financial institutions for as low as 35% of total debt · Legal fee 5% · T&C apply, case-by-case basis.",
        },
    ];

    return (
        <section
            id="packages"
            data-testid="packages-section"
            className="relative py-28 md:py-40 bg-[#0D1B1A] text-[#FDFAF5] overflow-hidden grain"
        >
            <div className="max-w-7xl mx-auto px-6 md:px-12 relative">
                <FadeUp>
                    <div className="flex items-center gap-3 mb-4">
                        <span className="ornament" />
                        <span className="text-[10px] tracking-[0.3em] uppercase text-[#D4944C]">
                            Chapter 03 · The Instruments
                        </span>
                    </div>
                    <h2 className="font-serif text-5xl md:text-6xl leading-[1.02] max-w-3xl">
                        Pick the{" "}
                        <span className="italic text-[#D4944C]">starting point</span>{" "}
                        that fits you.
                    </h2>
                    <p className="mt-6 text-[#9FB1AE] max-w-xl text-lg">
                        Every package starts with a questionnaire. You go
                        deeper only when you're ready.
                    </p>
                </FadeUp>

                <div className="mt-20 grid md:grid-cols-3 gap-6">
                    {pkgs.map((p, i) => (
                        <FadeUp key={p.key} delay={i * 0.08} y={40}>
                            <motion.div
                                whileHover={{ y: -6 }}
                                transition={{
                                    duration: 0.5,
                                    ease: easeOutExpo,
                                }}
                                data-testid={`package-card-${p.key}`}
                                className={`relative rounded-2xl p-8 md:p-10 h-full ${
                                    p.featured
                                        ? "bg-[#1A5C53] featured-glow md:-translate-y-6"
                                        : "bg-[#132927] border border-[#1F3533]"
                                }`}
                            >
                                {p.featured && (
                                    <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-[#D4944C] text-[#0D1B1A] text-[10px] tracking-[0.25em] uppercase font-medium px-4 py-1.5 rounded-full">
                                        Most Popular
                                    </div>
                                )}
                                <div className="text-[10px] tracking-[0.3em] uppercase text-[#D4944C] mb-3">
                                    {p.tag}
                                </div>
                                <h3 className="font-serif text-4xl md:text-5xl text-white">
                                    {p.name}
                                </h3>
                                <div className="mt-8 flex items-baseline gap-2">
                                    <span className="text-lg text-[#9FB1AE]">
                                        ₹
                                    </span>
                                    <span className="font-serif text-6xl md:text-7xl text-[#D4944C]">
                                        {p.price}
                                    </span>
                                </div>
                                <ul className="mt-10 space-y-3">
                                    {p.items.map((it) => (
                                        <li
                                            key={it}
                                            className="flex items-start gap-3 text-sm text-[#C4D3D1]"
                                        >
                                            <Check
                                                size={14}
                                                className="text-[#D4944C] mt-1 flex-shrink-0"
                                            />
                                            <span>{it}</span>
                                        </li>
                                    ))}
                                </ul>
                                {p.note && (
                                    <p className="mt-5 text-[10px] leading-relaxed text-[#9FB1AE] italic">
                                        {p.note}
                                    </p>
                                )}
                                <Link
                                    to={`/questionnaire/${p.key}`}
                                    data-testid={`package-cta-${p.key}`}
                                    className={`group mt-10 w-full inline-flex items-center justify-between rounded-full px-6 py-3.5 font-medium text-sm transition-colors ${
                                        p.featured
                                            ? "bg-[#D4944C] text-[#0D1B1A] hover:bg-[#E8B870]"
                                            : "bg-white/10 text-white hover:bg-white/20"
                                    }`}
                                >
                                    <span>Begin</span>
                                    <ArrowUpRight
                                        size={16}
                                        className="group-hover:rotate-45 transition-transform duration-500"
                                    />
                                </Link>
                            </motion.div>
                        </FadeUp>
                    ))}
                </div>
            </div>
        </section>
    );
}

/* ---------- About (text-forward, no photo) ---------- */

function AboutSection() {
    return (
        <section
            id="about"
            data-testid="about-section"
            className="py-28 md:py-40 bg-[#FDFAF5]"
        >
            <div className="max-w-7xl mx-auto px-6 md:px-12 grid md:grid-cols-12 gap-10 md:gap-16">
                <FadeUp className="md:col-span-4">
                    <div className="flex items-center gap-3 mb-4">
                        <span className="ornament" />
                        <span className="text-[10px] tracking-[0.3em] uppercase text-[#1A5C53]">
                            Chapter 04 · The Origin
                        </span>
                    </div>
                    <h2 className="font-serif text-4xl md:text-5xl leading-[1.02] text-[#0D1B1A]">
                        Two decades of numbers.{" "}
                        <span className="italic">
                            One mission: your peace of mind.
                        </span>
                    </h2>
                </FadeUp>
                <div className="md:col-span-7 md:col-start-6">
                    <FadeUp delay={0.1}>
                        <p className="text-lg text-[#4A5A58] leading-relaxed">
                            DhanNniti is built on 20+ years of hands-on finance
                            experience — from auditing Fortune-500 balance
                            sheets to sitting across the table with Indian
                            families and rewriting their debt story, one line
                            item at a time.
                        </p>
                    </FadeUp>
                    <FadeUp delay={0.15}>
                        <div className="mt-10 grid sm:grid-cols-2 gap-3">
                            {[
                                "20+ years in finance & audit",
                                "500+ families advised",
                                "₹50 Cr+ in restructured debt",
                                "Trained by top CA firms",
                            ].map((h) => (
                                <div
                                    key={h}
                                    className="flex items-start gap-3 border-t border-[#E6DFD3] pt-4"
                                >
                                    <Check
                                        size={14}
                                        className="text-[#1A5C53] mt-1.5 flex-shrink-0"
                                    />
                                    <span className="text-sm text-[#0D1B1A]">
                                        {h}
                                    </span>
                                </div>
                            ))}
                        </div>
                    </FadeUp>
                </div>
            </div>
        </section>
    );
}

/* ---------- Testimonials ---------- */

function TestimonialsSection() {
    const items = [
        {
            q: "I was paying minimums on 4 cards. In 14 months, I was completely credit-card-free. The plan was brutal but clear.",
            n: "Ananya M.",
            r: "Product Manager, Bengaluru",
        },
        {
            q: "They found ₹18,000/month of 'invisible' leaks in my budget. That alone paid off my personal loan 9 months early.",
            n: "Rohit & Priya S.",
            r: "Young family, Pune",
        },
        {
            q: "Our debts were beyond repair. DhanNniti's legal team helped us settle ₹18 lakhs for under ₹7 lakhs. The Samvad call was the turning point of our lives.",
            n: "Vikram R.",
            r: "Architect, Mumbai",
        },
    ];
    return (
        <section
            id="testimonials"
            data-testid="testimonials-section"
            className="py-28 md:py-40 bg-[#F3EFE9]/60"
        >
            <div className="max-w-7xl mx-auto px-6 md:px-12">
                <FadeUp>
                    <div className="flex items-center gap-3 mb-4">
                        <span className="ornament" />
                        <span className="text-[10px] tracking-[0.3em] uppercase text-[#1A5C53]">
                            Chapter 05 · The Field Notes
                        </span>
                    </div>
                    <h2 className="font-serif text-5xl md:text-6xl leading-[1.02] text-[#0D1B1A] max-w-3xl">
                        From red EMIs to a{" "}
                        <span className="italic">green calendar.</span>
                    </h2>
                </FadeUp>

                <div className="mt-16 grid md:grid-cols-3 gap-6">
                    {items.map((t, i) => (
                        <FadeUp key={i} delay={i * 0.08} y={40}>
                            <motion.div
                                whileHover={{ y: -4 }}
                                transition={{ duration: 0.5 }}
                                className="bg-white rounded-2xl border border-[#E6DFD3] p-8 h-full flex flex-col"
                            >
                                <Quote size={20} className="text-[#D4944C]" />
                                <p className="mt-4 font-serif text-xl text-[#0D1B1A] leading-snug flex-1">
                                    "{t.q}"
                                </p>
                                <div className="mt-6 pt-6 border-t border-[#E6DFD3]">
                                    <div className="font-medium text-[#0D1B1A]">
                                        {t.n}
                                    </div>
                                    <div className="text-xs text-[#758684] mt-1">
                                        {t.r}
                                    </div>
                                </div>
                            </motion.div>
                        </FadeUp>
                    ))}
                </div>
            </div>
        </section>
    );
}

/* ---------- FAQ (custom accordion with motion) ---------- */

function FAQSection() {
    const [open, setOpen] = useState(0);
    const faqs = [
        {
            q: "Is DhanNniti a loan-selling or lending company?",
            a: "No. We are strictly an advisory service. We don't sell loans, insurance, or investments. Our only product is clarity.",
        },
        {
            q: "How confidential is my financial data?",
            a: "100%. Your questionnaire is encrypted, never sold, never shared. Only the founder and the assigned strategist see your numbers.",
        },
        {
            q: "How long until I'm debt-free?",
            a: "Depends on your numbers — typically 12 to 36 months once you follow the plan. We give you a realistic target date, not a fantasy.",
        },
        {
            q: "What if I can't afford even the Disha package right now?",
            a: "Reach out via the consultation form. We have community resources and free content on our blog that can help you start.",
        },
        {
            q: "Do you work with people outside India?",
            a: "Yes — NRIs with India-based debt or assets are welcome. Most tools and negotiation tactics apply globally with minor tweaks.",
        },
    ];

    return (
        <section
            id="faq"
            data-testid="faq-section"
            className="py-28 md:py-40 bg-[#FDFAF5]"
        >
            <div className="max-w-4xl mx-auto px-6 md:px-12">
                <FadeUp>
                    <div className="flex items-center gap-3 mb-4">
                        <span className="ornament" />
                        <span className="text-[10px] tracking-[0.3em] uppercase text-[#1A5C53]">
                            Chapter 06 · The Objections
                        </span>
                    </div>
                    <h2 className="font-serif text-5xl md:text-6xl leading-[1.02] text-[#0D1B1A]">
                        Questions,{" "}
                        <span className="italic">answered honestly.</span>
                    </h2>
                </FadeUp>

                <div className="mt-16 divide-y divide-[#0D1B1A]/12">
                    {faqs.map((f, i) => {
                        const isOpen = open === i;
                        return (
                            <FadeUp key={i} delay={i * 0.04}>
                                <button
                                    onClick={() => setOpen(isOpen ? -1 : i)}
                                    data-testid={`faq-item-${i}`}
                                    className="w-full py-7 flex items-start justify-between gap-6 text-left group"
                                >
                                    <span
                                        className={`font-serif text-2xl md:text-3xl leading-tight transition-colors ${isOpen ? "text-[#1A5C53]" : "text-[#0D1B1A] group-hover:text-[#1A5C53]"}`}
                                    >
                                        {f.q}
                                    </span>
                                    <span className="flex-shrink-0 mt-2">
                                        {isOpen ? (
                                            <Minus
                                                size={20}
                                                className="text-[#D4944C]"
                                            />
                                        ) : (
                                            <Plus
                                                size={20}
                                                className="text-[#0D1B1A]/50 group-hover:text-[#D4944C] transition-colors"
                                            />
                                        )}
                                    </span>
                                </button>
                                <AnimatePresence initial={false}>
                                    {isOpen && (
                                        <motion.div
                                            initial={{
                                                height: 0,
                                                opacity: 0,
                                            }}
                                            animate={{
                                                height: "auto",
                                                opacity: 1,
                                            }}
                                            exit={{ height: 0, opacity: 0 }}
                                            transition={{
                                                duration: 0.5,
                                                ease: easeOutExpo,
                                            }}
                                            className="overflow-hidden"
                                        >
                                            <p className="text-[#4A5A58] text-lg pb-8 max-w-2xl leading-relaxed">
                                                {f.a}
                                            </p>
                                        </motion.div>
                                    )}
                                </AnimatePresence>
                            </FadeUp>
                        );
                    })}
                </div>
            </div>
        </section>
    );
}

/* ---------- Final CTA ---------- */

function FinalCTA() {
    const ref = useRef(null);
    const { scrollYProgress } = useScroll({
        target: ref,
        offset: ["start end", "end start"],
    });
    const bgY = useTransform(scrollYProgress, [0, 1], ["-15%", "15%"]);

    return (
        <section
            ref={ref}
            data-testid="final-cta-section"
            className="relative py-32 md:py-48 bg-[#0D1B1A] text-white overflow-hidden grain"
        >
            <motion.div
                style={{ y: bgY }}
                className="absolute inset-0 opacity-15"
            >
                <img
                    src="https://images.unsplash.com/photo-1524673450801-b5aa9b621b76?auto=format&fit=crop&w=2400&q=80"
                    alt=""
                    className="w-full h-full object-cover"
                />
            </motion.div>
            <div className="relative max-w-4xl mx-auto px-6 md:px-12 text-center">
                <FadeUp>
                    <h2 className="font-serif text-6xl md:text-8xl leading-[0.95]">
                        Your debt-free date is
                        <br />
                        <span className="italic text-[#D4944C]">
                            closer than you think.
                        </span>
                    </h2>
                </FadeUp>
                <FadeUp delay={0.1}>
                    <p className="mt-8 text-lg text-[#C4D3D1] max-w-2xl mx-auto">
                        One questionnaire. One strategist. One roadmap. That's
                        all it takes to trade EMI anxiety for quiet confidence.
                    </p>
                </FadeUp>
                <FadeUp delay={0.2}>
                    <div className="mt-12 flex flex-col sm:flex-row gap-4 justify-center">
                        <a
                            href="#packages"
                            data-testid="final-cta-book"
                            className="group bg-[#D4944C] text-[#0D1B1A] hover:bg-[#E8B870] transition-colors rounded-full px-8 py-4 font-medium inline-flex items-center gap-3"
                        >
                            Choose your package
                            <ArrowUpRight
                                size={16}
                                className="group-hover:rotate-45 transition-transform duration-500"
                            />
                        </a>
                        <Link
                            to="/questionnaire/disha"
                            data-testid="final-cta-disha"
                            className="border border-white/30 text-white hover:bg-white/10 rounded-full px-8 py-4 font-medium inline-flex items-center gap-3"
                        >
                            Start with Disha — ₹969
                        </Link>
                    </div>
                </FadeUp>
            </div>
        </section>
    );
}

/* ---------- Root ---------- */

export default function Landing() {
    // Scroll to hash target after mount
    useEffect(() => {
        if (window.location.hash) {
            const t = setTimeout(() => {
                window.__lenisScrollTo?.(window.location.hash);
            }, 400);
            return () => clearTimeout(t);
        }
    }, []);

    return (
        <div className="bg-[#FDFAF5] text-[#0D1B1A]">
            <Navbar />
            <Hero />
            <Marquee />
            <ProblemSection />
            <ManifestoSection />
            <PackagesSection />
            <AboutSection />
            <TestimonialsSection />
            <FAQSection />
            <FinalCTA />
            <Footer />
        </div>
    );
}
